import { Asset } from '@llx/common';
import { CaseMap } from '@llx/common';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  AssetViewFieldsFragment,
  AssetViewWhereInput,
  ListAssetViewsDocument,
  ListAssetViewsQuery,
  ListAssetViewsQueryVariables,
} from '@/@generated/codegen/API';
import { useEntityFetchAll } from '@/hooks/useEntityFetchAll';
import { useEntitySubscription } from '@/hooks/useEntitySubscription';
import { captureSentry } from '@/utils/helpers/sentryHelper';

import { parseAssetViewsPerCase } from '../utils/parseAssetViewsPerCase';

export default function useMeetingAssetStats(
  activated: boolean,
  requirementsAreLoading: boolean,
  meetingID: string | undefined,
  caseMap: CaseMap,
  caseIds: string[]
) {
  const [assetViewsPerCase, setAssetViewsPerCase] = useState<
    Record<string, AssetViewFieldsFragment[]>
  >({});
  const where: AssetViewWhereInput = useMemo(() => {
    return {
      meetingId: {
        equals: meetingID,
      },
    };
  }, [meetingID]);
  const onResult = useCallback(
    (result: AssetViewFieldsFragment[]) => {
      const newAssetViewsPerCase: Record<string, AssetViewFieldsFragment[]> = result.reduce(
        (acc, curr) => {
          if (!acc[curr.patientCaseId]) acc[curr.patientCaseId] = [];
          acc[curr.patientCaseId].push(curr);
          return acc;
        },
        {} as Record<string, AssetViewFieldsFragment[]>
      );
      // NOTE: make sure that all caseIds have an entry in the map
      for (const caseId of caseIds) {
        if (!newAssetViewsPerCase[caseId]) newAssetViewsPerCase[caseId] = [];
      }
      setAssetViewsPerCase(newAssetViewsPerCase);
    },
    [caseIds]
  );

  const { fetchAll: fetchAllAssetViews } = useEntityFetchAll<
    ListAssetViewsQuery,
    ListAssetViewsQueryVariables,
    AssetViewWhereInput,
    AssetViewFieldsFragment
  >(
    where,
    ListAssetViewsDocument,
    (data) => data.listAssetViews.edges.map((el) => el.node),
    (data) => data.listAssetViews.pageInfo,
    onResult
  );

  const parsedAssetViews = useMemo(() => {
    //we don't want to continue if anything needed is still loading
    if (
      !activated ||
      requirementsAreLoading ||
      !caseMap ||
      Object.keys(assetViewsPerCase).length !== caseIds.length
    )
      return undefined;
    const caseIdToAssetList: Record<string, Asset[]> = {};
    for (const caseId of caseIds) {
      caseIdToAssetList[caseId] = caseMap[caseId].assets || [];
    }
    return parseAssetViewsPerCase(caseIds, caseIdToAssetList, assetViewsPerCase);
  }, [caseIds, assetViewsPerCase, requirementsAreLoading, activated]);

  useEntitySubscription('AssetViews', {
    onCreate: {
      variables: {
        where,
      },
      onData: (res) => {
        const obj = res.data.data?.onCreateAssetView;
        if (!obj) {
          captureSentry({
            title: 'useMeetingAssetStats | onData | obj is undefined',
            detail: {},
          });
          return;
        }
        setAssetViewsPerCase((prev) => {
          const newObj = { ...prev };
          if (!newObj[obj.patientCaseId]) newObj[obj.patientCaseId] = [];
          newObj[obj.patientCaseId].push(obj);
          return newObj;
        });
      },
    },
  });

  useEffect(() => {
    if (!activated || requirementsAreLoading) return;
    void fetchAllAssetViews();
  }, [where, activated, requirementsAreLoading, fetchAllAssetViews]);

  return {
    parsedAssetViews,
  };
}
